/**
 * @fileoverview Universal Detail Page Component
 * 
 * A universal, configurable detail page component that can be used for any entity type.
 * Consumes UniversalEntityConfig to render appropriate sections and behaviors.
 * 
 * Features:
 * - Config-driven layout and fields
 * - Automatic data fetching
 * - Inter-page navigation with filter support
 * - Editable fields with inline editing
 * - Optional email, notes, documents sections
 * - Plugin system for custom behaviors
 * - Mobile responsive
 * 
 * @module Components/UniversalDetailPage
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Input } from "@heroui/react";
import { Badge } from "components/ui/badge";
import { ArrowLeft, PencilSimple, Check, X, CaretDown } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { UniversalEntityConfig } from "shared/types/universalEntityConfig";
import { EditableDetailField } from "shared/components/EditableDetailField";
import { InterPageNavigation } from "shared/components/interPageNavigation";
import { UniversalEmailSection } from "./components/UniversalEmailSection";
import DocumentsList from "components/documents/documentsList";
import FileUploadComponent from "components/documents/fileUpload";
import { ResourceType } from "shared/types/resource";
import { Loader2 } from "lucide-react";

interface UniversalDetailPageProps {
  /** Entity configuration */
  entityConfig: UniversalEntityConfig;

  /** Optional: Override entity ID from URL */
  entityId?: string | number;

  /** Optional: Pre-loaded entity data */
  initialEntity?: any;

  /** Optional: Additional data needed for navigation/filtering */
  allEntities?: any[];

  /** Optional: Custom className */
  className?: string;

  /** Optional: Render custom content after the fields in the details section */
  renderAfterFields?: (entity: any, onUpdate: (updated: any) => void) => React.ReactNode;
}

export const UniversalDetailPage: React.FC<UniversalDetailPageProps> = ({
  entityConfig,
  entityId: propEntityId,
  initialEntity,
  allEntities = [],
  className = "",
  renderAfterFields,
}) => {
  // ============ Routing & Session ============
  const { id: urlId } = useParams<{ id: string }>();
  const entityId = propEntityId || urlId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session } = useSession();
  
  // ============ State ============
  const [entity, setEntity] = useState<any>(initialEntity || null);
  const [isLoading, setIsLoading] = useState(!initialEntity);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(true);
  const loadedEntityIdRef = useRef<string | null>(null);
  
  // ============ Derived Data ============
  const entityTitle = typeof entityConfig.titleField === 'function'
    ? entityConfig.titleField(entity)
    : entity?.[entityConfig.titleField] || '';
  
  const entityDisplayId = entityConfig.entityPrefix
    ? `${entityConfig.entityPrefix}${entity?.id}`
    : `${entity?.id}`;

  // Get title icon
  const TitleIcon = useMemo(() => {
    if (!entity || !entityConfig.getTitleIcon) return null;
    return entityConfig.getTitleIcon(entity);
  }, [entity, entityConfig]);

  // Get metadata chips
  const metadataChips = useMemo(() => {
    if (!entity || !entityConfig.getMetadataChips) return [];
    return entityConfig.getMetadataChips(entity).filter(chip => chip.condition !== false);
  }, [entity, entityConfig]);
  
  // Convert UniversalFieldConfig to EditableFieldConfig format
  const convertToEditableConfig = (field: any) => ({
    id: field.id,
    label: field.label,
    type: field.type,
    icon: field.icon,
    pickerMode: field.pickerMode,
    pickerConfig: field.pickerConfig, // Pass picker configuration (customOptions, multiSelect, etc.)
    displayValue: (entity: any) => field.formatDisplay(field.getValue(entity), entity),
    extractValue: (entity: any) => field.getValue(entity),
    buildUpdatePayload: field.buildUpdatePayload,
    customUpdateHandler: field.customUpdateHandler,
    extractContextValue: field.extractContextValue,
    placeholder: field.detailConfig?.placeholder,
    validate: field.detailConfig?.validate,
    className: field.detailConfig?.className,
    chipColor: field.detailConfig?.chipColor,
    customDisplayRenderer: field.detailConfig?.customDisplayRenderer,
    // Optional per-entity disable predicate (e.g. "field locked by approval")
    getDisabled: field.detailConfig?.getDisabled,
  });
  
  // Get fields by section and convert them
  const mainFields = entityConfig.fields
    .filter(f => !f.detailConfig?.section || f.detailConfig?.section === 'main')
    .map(convertToEditableConfig);
  
  // Get notes field (if single textarea field)
  const notesFieldRaw = entityConfig.fields.find(f => 
    f.type === 'textarea' && (!f.detailConfig?.section || f.detailConfig?.section === 'main')
  );
  const notesField = notesFieldRaw ? convertToEditableConfig(notesFieldRaw) : null;
  
  // Separate note from other main fields
  const mainFieldsWithoutNote = mainFields.filter(f => f.id !== notesField?.id);
  
  // ============ Navigation & Filtering ============
  const filteredEntitiesForNav = useMemo(() => {
    if (!entityConfig.navigation.useFiltering || !allEntities.length) {
      return allEntities;
    }
    
    // Use custom filter logic if provided
    if (entityConfig.navigation.customFilterLogic) {
      return entityConfig.navigation.customFilterLogic(allEntities, searchParams, entityId as string);
    }
    
    // Default: return all entities (no filtering)
    return allEntities;
  }, [allEntities, searchParams, entityId, entityConfig]);
  
  const currentEntityInFiltered = filteredEntitiesForNav.find(e => e.id?.toString() === entityId?.toString());

  // ============ Data Fetching ============
  const fetchEntity = useCallback(async () => {
    if (!entityId) {
      setIsLoading(false);
      return;
    }

    // Skip if already loaded - use ref to avoid infinite loop
    if (loadedEntityIdRef.current === entityId?.toString()) {
      console.log(`⏭️  Skipping refetch for ${entityConfig.entityName} ${entityId} (already loaded)`);
      return;
    }

    // Reset state
    setEntity(null);
    setIsLoading(true);
    setIsEditingTitle(false);
    setEditedTitle('');

    console.log(`📦 Fetching ${entityConfig.entityName} ${entityId}`);

    try {
      const endpoint = entityConfig.api.fetchOne(entityId);
      const response = await fetchWithAuth(endpoint, { method: "GET" }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch ${entityConfig.entityName}`);
      }

      let data = await response.json();

      // Transform data if plugin provides transformer
      if (entityConfig.plugins?.transformEntityData) {
        data = entityConfig.plugins.transformEntityData(data);
      }

      // Compute display ID from fresh data
      const displayId = entityConfig.entityPrefix
        ? `${entityConfig.entityPrefix}${data.id}`
        : `${data.id}`;

      console.log(`✅ ${entityConfig.entityName} loaded: ${displayId}`);
      setEntity(data);
      loadedEntityIdRef.current = entityId?.toString() || null;

      // Run onEntityLoad plugin
      if (entityConfig.plugins?.onEntityLoad) {
        await entityConfig.plugins.onEntityLoad(data, { session, navigate });
      }
    } catch (error) {
      console.error(`❌ Error fetching ${entityConfig.entityName} ${entityId}:`, error);
      toast.error(`Fout bij laden van ${entityConfig.displayName.toLowerCase()}`);
      navigate(entityConfig.navigation.backRoute);
    } finally {
      setIsLoading(false);
    }
  }, [entityId, session, entityConfig, navigate]);
  
  // Fetch entity on mount or when entityId/initialEntity changes
  useEffect(() => {
    if (initialEntity) {
      // If we have initialEntity and the entityId matches, use it
      if (initialEntity.id?.toString() === entityId?.toString()) {
        setEntity(initialEntity);
        setIsLoading(false);
        loadedEntityIdRef.current = entityId?.toString() || null;
      }
    } else {
      // No initialEntity - fetch from API (skip logic inside fetchEntity prevents duplicates)
      fetchEntity();
    }
  }, [initialEntity, entityId, fetchEntity]);
  
  // Update title when entity changes
  useEffect(() => {
    if (entity) {
      setEditedTitle(entityTitle);
    }
  }, [entity, entityTitle]);
  
  // ============ Title Editing ============
  const handleTitleEdit = () => {
    setEditedTitle(entityTitle);
    setIsEditingTitle(true);
  };
  
  const handleTitleSave = async () => {
    if (!editedTitle.trim() || !entity) return;
    
    const titleFieldName = typeof entityConfig.titleField === 'string' ? entityConfig.titleField : 'title';
    
    try {
      const response = await fetchWithAuth(
        entityConfig.api.update(entity.id),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [titleFieldName]: editedTitle.trim() }),
        },
        session
      );
      
      if (response.ok) {
        const updatedEntity = await response.json();
        setEntity(updatedEntity);
        setIsEditingTitle(false);
        toast.success('Titel bijgewerkt');
        
        // Run onEntityUpdate plugin
        if (entityConfig.plugins?.onEntityUpdate) {
          await entityConfig.plugins.onEntityUpdate(updatedEntity, { session, navigate });
        }
      } else {
        throw new Error('Failed to update title');
      }
    } catch (error) {
      console.error('Error updating title:', error);
      toast.error('Fout bij bijwerken van titel');
    }
  };
  
  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditedTitle(entityTitle);
  };
  
  // ============ Navigation ============
  const handleNavigateToEntity = useCallback((targetId: string | number) => {
    if (entityConfig.navigation.customNavigationHandler) {
      entityConfig.navigation.customNavigationHandler(targetId, searchParams, navigate);
    } else {
      // Preserve search params
      const params = new URLSearchParams(searchParams);
      const basePath = entityConfig.navigation.backRoute;
      navigate(`${basePath}/${targetId}?${params.toString()}`);
    }
  }, [entityConfig, searchParams, navigate]);
  
  const handleBackClick = () => {
    // Preserve search params when navigating back to list
    const params = new URLSearchParams(searchParams);
    const backRoute = entityConfig.navigation.backRoute;
    navigate(`${backRoute}?${params.toString()}`);
  };
  
  // ============ Custom Actions ============
  const customActions = useMemo(() => {
    if (!entityConfig.plugins?.actions || !entity) return [];
    return entityConfig.plugins.actions.filter(action => 
      !action.condition || action.condition(entity)
    );
  }, [entityConfig.plugins?.actions, entity]);
  
  const handleCustomAction = async (action: any) => {
    if (action.confirmMessage && !window.confirm(action.confirmMessage)) {
      return;
    }
    
    try {
      await action.handler(entity, session, setEntity);
    } catch (error) {
      console.error(`Error executing action ${action.id}:`, error);
      toast.error(`Fout bij uitvoeren van actie`);
    }
  };
  
  // ============ Custom Sections ============
  const customSections = useMemo(() => {
    if (!entityConfig.plugins?.customSections || !entity) return [];
    return entityConfig.plugins.customSections.filter(section =>
      !section.condition || section.condition(entity)
    );
  }, [entityConfig.plugins?.customSections, entity]);

  // ============ Performance: Memoized Styles ============
  // CRITICAL: Memoize style objects to prevent React from updating DOM on every render
  // which triggers expensive layout recalculations
  const hasSidebar = customSections.some(s => s.position === 'sidebar');

  // Full layout containment - creates separate layout context
  // Sidebar layout changes NEVER affect main content layout
  const sidebarContainerStyle = useMemo(() =>
    hasSidebar ? {
      contain: 'layout style size' as const,  // Full isolation
      maxHeight: 'calc(100vh - 200px)',
      overflowY: 'auto' as const
    } : undefined,
    [hasSidebar]
  );

  // Main content containment - isolates from sidebar
  const mainContentStyle = useMemo(() =>
    hasSidebar ? {
      contain: 'layout style' as const
    } : undefined,
    [hasSidebar]
  );

  const renderCustomSection = (position: string) => {
    return customSections
      .filter(section => section.position === position)
      .map(section => {
        const Component = section.component;
        return (
          <div key={section.id} className="mb-3 sm:mb-6">
            <Component 
              entity={entity}
              onUpdate={setEntity}
              {...(section.componentProps || {})}
            />
          </div>
        );
      });
  };
  
  // ============ Loading State ============
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-neutral-secondary-mid">
            {entityConfig.loadingText || `${entityConfig.displayName} laden...`}
          </p>
        </div>
      </div>
    );
  }
  
  // ============ Not Found State ============
  if (!entity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">❌</div>
          <p className="font-medium mb-2">
            {entityConfig.notFoundText || `${entityConfig.displayName} niet gevonden`}
          </p>
          <Button onClick={handleBackClick} className="mt-4">
            Terug naar {entityConfig.displayNamePlural.toLowerCase()}
          </Button>
        </div>
      </div>
    );
  }
  
  // ============ Main Render ============
  return (
    <div className={`min-h-screen ${className}`}>
      {/* Header */}
      <div className="sticky-below-header bg-white bleed-full-width border-b border-neutral-primary/10 py-3 sm:py-6 mb-6">
        {/* Mobile Layout */}
        <div className="block lg:hidden">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Button
                onClick={handleBackClick}
                className="text-neutral-secondary-mid hover:text-brick-primary"
                size="sm"
              >
                <ArrowLeft size={18} />
              </Button>
              <div className="flex items-center gap-2">
                {TitleIcon ? (
                  <TitleIcon size={22} weight="duotone" className="text-brick-primary flex-shrink-0" />
                ) : (
                  <div className="w-2 h-6 bg-brick-primary rounded-full"></div>
                )}
                {entityConfig.titleFirst ? (
                  <div>
                    <div className="text-lg font-bold text-neutral-primary">
                      {entityTitle}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {entityDisplayId}
                    </div>
                  </div>
                ) : (
                  <div className="text-lg font-bold text-neutral-primary">
                    {entityDisplayId}
                  </div>
                )}
              </div>
            </div>
            
            {/* Mobile actions */}
            <div className="flex items-center gap-2">
              {/* Inter-page Navigation - Mobile */}
              {entityConfig.navigation.enableInterPageNav && filteredEntitiesForNav.length > 1 && currentEntityInFiltered && (
                <InterPageNavigation
                  currentItemId={entityId as string | number}
                  filteredItems={filteredEntitiesForNav}
                  onNavigateToItem={handleNavigateToEntity}
                  getSortValue={(e) => new Date(e.createdAt || e.created_at || '')}
                  sortDirection="desc"
                  compact={true}
                  labels={{ previous: 'Vorig', next: 'Volgend' }}
                  hideWhenSingle={false}
                />
              )}
              
              {/* Custom sections - header-right mobile */}
              {renderCustomSection('header-right')}
              
              {/* Custom action buttons - Mobile */}
              {customActions.map(action => (
                <Button
                  key={action.id}
                  onClick={() => handleCustomAction(action)}
                  size="sm"
                  className="scale-75"
                >
                  {action.icon && <action.icon size={16} />}
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Title row - hidden when titleFirst since title is already in the header */}
          {!entityConfig.titleFirst && (
            <div className="mb-3">
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleTitleSave();
                      if (e.key === 'Escape') handleTitleCancel();
                    }}
                    className="flex-1"
                    autoFocus
                    classNames={{
                      input: "text-base font-semibold",
                      inputWrapper: "border-moss-primary"
                    }}
                  />
                  <Button size="sm" onClick={handleTitleSave} className="bg-moss-primary text-white">
                    <Check size={14} />
                  </Button>
                  <Button size="sm" onClick={handleTitleCancel} className="text-neutral-secondary-mid">
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <h1 className="text-base font-semibold text-neutral-primary">
                    {entityTitle}
                  </h1>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onClick={handleTitleEdit}
                    className="text-neutral-secondary-mid hover:text-moss-primary opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <PencilSimple size={12} />
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Metadata chips - Mobile */}
          <div className="flex flex-wrap items-center gap-1">
            {metadataChips.map((chip, idx) => {
              const Icon = chip.icon;
              const badge = (
                <Badge
                  key={idx}
                  variant="outline"
                  className={`text-xs ${chip.href ? 'cursor-pointer hover:opacity-80' : ''} ${chip.color || 'border-neutral-400 bg-neutral-50 text-neutral-700'}`}
                >
                  {Icon && <Icon size={10} className="mr-1" />}
                  {chip.label}
                </Badge>
              );
              return chip.href ? (
                <a key={idx} href={chip.href} target="_blank" rel="noopener noreferrer">
                  {badge}
                </a>
              ) : badge;
            })}
          </div>
        </div>
        
        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="flex items-start justify-between mb-4">
            {/* Left side */}
            <div className="flex items-start gap-4 flex-1">
              <Button
                isIconOnly
                variant="light"
                onClick={handleBackClick}
                className="text-neutral-secondary-mid hover:text-brick-primary mt-1"
              >
                <ArrowLeft size={20} />
              </Button>
              <div className="flex-1">
                {/* Title section */}
                <div className="flex items-center gap-3 mb-3">
                  {TitleIcon ? (
                    <TitleIcon size={28} weight="duotone" className="text-brick-primary flex-shrink-0" />
                  ) : (
                    <div className="w-3 h-8 bg-brick-primary rounded-full"></div>
                  )}
                  {entityConfig.titleFirst ? (
                    /* Title-first layout: name is the main heading, ID is subtle */
                    <div className="flex-1">
                      {isEditingTitle ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleTitleSave();
                              if (e.key === 'Escape') handleTitleCancel();
                            }}
                            className="flex-1"
                            autoFocus
                            classNames={{
                              input: "text-2xl font-bold",
                              inputWrapper: "border-moss-primary"
                            }}
                          />
                          <Button isIconOnly size="sm" onClick={handleTitleSave} className="bg-moss-primary text-white">
                            <Check size={16} />
                          </Button>
                          <Button isIconOnly size="sm" variant="light" onClick={handleTitleCancel} className="text-neutral-secondary-mid">
                            <X size={16} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <h1 className="text-2xl font-bold text-neutral-primary">
                            {entityTitle}
                          </h1>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onClick={handleTitleEdit}
                            className="text-neutral-secondary-mid hover:text-moss-primary opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <PencilSimple size={14} />
                          </Button>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {entityDisplayId}
                      </p>
                    </div>
                  ) : (
                    /* Default layout: ID is prominent, title next to it */
                    <>
                      <div className="text-2xl font-bold text-neutral-primary">
                        {entityDisplayId}
                      </div>
                      {isEditingTitle ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editedTitle}
                            onChange={(e) => setEditedTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleTitleSave();
                              if (e.key === 'Escape') handleTitleCancel();
                            }}
                            className="flex-1"
                            autoFocus
                            classNames={{
                              input: "text-lg font-semibold",
                              inputWrapper: "border-moss-primary"
                            }}
                          />
                          <Button isIconOnly size="sm" onClick={handleTitleSave} className="bg-moss-primary text-white">
                            <Check size={16} />
                          </Button>
                          <Button isIconOnly size="sm" variant="light" onClick={handleTitleCancel} className="text-neutral-secondary-mid">
                            <X size={16} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-1 group">
                          <h1 className="text-lg font-semibold text-neutral-primary">
                            {entityTitle}
                          </h1>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onClick={handleTitleEdit}
                            className="text-neutral-secondary-mid hover:text-moss-primary opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <PencilSimple size={14} />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Metadata chips - Desktop */}
                <div className="flex flex-wrap items-center gap-2">
                  {metadataChips.map((chip, idx) => {
                    const Icon = chip.icon;
                    const badge = (
                      <Badge
                        key={idx}
                        variant="outline"
                        className={`${chip.href ? 'cursor-pointer hover:opacity-80' : ''} ${chip.color || 'border-neutral-400 bg-neutral-50 text-neutral-700'}`}
                      >
                        {Icon && <Icon size={12} className="mr-1" />}
                        {chip.label}
                      </Badge>
                    );
                    return chip.href ? (
                      <a key={idx} href={chip.href} target="_blank" rel="noopener noreferrer">
                        {badge}
                      </a>
                    ) : badge;
                  })}
                </div>
              </div>
            </div>
            
            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Inter-page Navigation - Desktop */}
              {entityConfig.navigation.enableInterPageNav && filteredEntitiesForNav.length > 1 && currentEntityInFiltered && (
                <InterPageNavigation
                  currentItemId={entityId as string | number}
                  filteredItems={filteredEntitiesForNav}
                  onNavigateToItem={handleNavigateToEntity}
                  getSortValue={(e) => new Date(e.createdAt || e.created_at || '')}
                  sortDirection="desc"
                  compact={false}
                  labels={{ previous: `Vorig ${entityConfig.displayName.toLowerCase()}`, next: `Volgend ${entityConfig.displayName.toLowerCase()}` }}
                  hideWhenSingle={false}
                />
              )}
              
              {/* Custom sections - header-right */}
              {renderCustomSection('header-right')}
              
              {/* Custom action buttons - Desktop */}
              <div className="flex items-center gap-3">
                {customActions.map(action => (
                  <Button
                    key={action.id}
                    onClick={() => handleCustomAction(action)}
                    startContent={action.icon && <action.icon size={20} />}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Custom sections - before-content */}
        {renderCustomSection('before-content')}
      </div>
      
      {/* Main Content and Sidebar Layout */}
      <div className={`max-w-full mx-auto px-3 sm:px-6 pb-6 ${hasSidebar ? 'lg:grid lg:grid-cols-[1fr_384px] lg:gap-6' : ''}`}>
        {/* Main Content */}
        <div style={mainContentStyle}>
          {/* Email Section */}
        {entityConfig.emails?.enabled && (
          <div className="mb-3 sm:mb-6">
            <UniversalEmailSection
              entity={entity}
              entityConfig={entityConfig}
              onEntityUpdate={setEntity}
            />
          </div>
        )}
        
        {/* Editable Fields Section */}
        {(mainFieldsWithoutNote.length > 0 || notesField) && (
          <div className="bg-white rounded-lg border border-neutral-primary/15 shadow-sm overflow-hidden mb-3 sm:mb-6">
            <button
              onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
              className="w-full border-b border-neutral-primary/10 px-4 py-3 hover:bg-white transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PencilSimple size={18} className="text-moss-primary" weight="duotone" />
                  <h3 className="text-base font-semibold text-neutral-primary">
                    {entityConfig.displayName} Details
                  </h3>
                </div>
                <CaretDown
                  size={20}
                  className={`text-neutral-secondary-mid transition-transform ${
                    isDetailsExpanded ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </button>

            {isDetailsExpanded && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Left Column - Regular fields */}
                <div className="divide-y divide-neutral-primary/10 border-r border-neutral-primary/10">
                  {mainFieldsWithoutNote.map((fieldConfig) => (
                    <div key={fieldConfig.id} className="px-4 py-3">
                      <EditableDetailField
                        config={fieldConfig}
                        entity={entity}
                        onUpdate={setEntity}
                        apiEndpoint={entityConfig.api.update(entity.id)}
                        compact={true}
                        disabled={!!(fieldConfig as any).getDisabled?.(entity)}
                      />
                    </div>
                  ))}
                  {/* Custom content after fields */}
                  {renderAfterFields && (
                    <div className="px-4 py-3">
                      {renderAfterFields(entity, setEntity)}
                    </div>
                  )}
                </div>

                {/* Right Column - Notes field */}
                {notesField && (!entity || (notesField.extractValue(entity) !== null && notesField.extractValue(entity) !== undefined)) && (
                  <div className="px-4 py-3 flex flex-col h-full">
                    <div className="flex-1 flex flex-col">
                      <EditableDetailField
                        config={notesField}
                        entity={entity}
                        onUpdate={setEntity}
                        apiEndpoint={entityConfig.api.update(entity.id)}
                        compact={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Custom sections - after-content */}
        {renderCustomSection('after-content')}
        
        {/* Documents Section */}
        {entityConfig.documents?.enabled && entity.id && (
          <div className="mb-3 sm:mb-6">
            <div className="flex flex-col gap-2 rounded-md border border-neutral-primary/15 bg-white pb-3">
              <DocumentsList
                sharePointFolderId={(entityConfig.documents as any).sharePointConfig?.getFolderId(entity)}
                sharePointFolderUrl={(entityConfig.documents as any).sharePointConfig?.getFolderUrl(entity)}
              />
              <FileUploadComponent
                resourceType={entityConfig.entityName.toUpperCase() as ResourceType}
                resource={entity}
                onUploadSuccess={() => {
                  // Optionally refresh entity to show new document
                  console.log('Document uploaded successfully');
                }}
                buildingId={entity.buildingId}
                unitId={entity.unitId}
                assetId={entityConfig.entityName === 'asset' ? entity.id : undefined}
              />
            </div>
          </div>
        )}
        </div>

        {/* Sidebar Sections */}
        {hasSidebar && (
          <div
            style={sidebarContainerStyle}
          >
            {renderCustomSection('sidebar')}
          </div>
        )}
      </div>
   </div>
  );
};

